import { Component, OnInit } from '@angular/core';
import {
  BesharpTableComponent,
  BesharpTableHeaderDefinition,
  BesharpTableSize,
} from '../../modules/besharp-table/component/besharp-table.component';
import { MessageService, ConfirmationService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { SoftwareUpdateService } from '../../services/software-update.service';
import { UpdateFileParserService } from '../../services/update-file-parser.service';
import { filterMenderFamilies } from '../../utils/family-routing';
import { CognitoService } from '../../services/cognito.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { NgIf, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MenuModule } from 'primeng/menu';
import { fetchAuthSession } from 'aws-amplify/auth';

export type SoftwareUpdate = {
  id: number;
  swVersion: string;
  description: string;
  supportedMachines: string[];
  uploadDate: string;
  updateType: 'USER_CONFIRM' | 'FORCE_UPDATE';
};

@Component({
  standalone: true,
  imports: [
    CardModule,
    BesharpTableComponent,
    ButtonModule,
    ToastModule,
    NgClass,
    NgIf,
    DialogModule,
    FormsModule,
    ProgressSpinnerModule,
    SelectModule,
    ConfirmDialogModule,
    MenuModule,
  ],
  providers: [MessageService, ConfirmationService],
  selector: 'app-software-updates',
  templateUrl: './software-updates.component.html',
  styleUrls: ['./software-updates.component.scss'],
})
export class SoftwareUpdatesComponent implements OnInit {
  softwareUpdates: SoftwareUpdate[] = [];
  eBesharpTableSize = BesharpTableSize;

  datasource: SoftwareUpdate[] = [];
  dataSourceLength = 0;
	orderColumn: string = 'release_date';
	orderDirection: 'asc' | 'desc' = 'desc';
  filters: { [key: string]: any } = {};
  loading = false;
  loadingFamilyMenu = false;
  pageRows = 10;
  offset = 0;

  showUploadModal = false;
  showFormStep = false;
  successModalVisible = false;
  showEditModal = false;
  editingValue = '';
  editing = false;
  uploading = false;

  isDragOver = false;
  uploadedFile: File | null = null;
  sha256: string = '';
  parsedFileInfo: any = null;
  
  userProvidedSha256: string = '';
  calculatedSha256: string = '';
  sha256ValidationError: string = '';
  isValidatingSha256: boolean = false;

  form = {
    family: '',
    model: '',
    versionNumber: '',
    description: '',
  };

  editForm = {
    editing: '',
  };

  families: string[] = [];
  selectedFamilyForUpload: string = '';
  showFamilySelection = false;
  
  familyMenuItems: any[] = [];

  canManageSoftware = false;

  tableDefinition: BesharpTableHeaderDefinition[] = [
    { def: 'versionNumber', title: 'Version', sortable: true, columnFilter: true },
    { def: 'description', title: 'Description', sortable: true, columnFilter: true, transformer: (desc: string) => `${desc}` },
    {
      def: 'supportedMachines',
      title: 'Supported Machines',
      sortable: false,
      columnFilter: true,
      transformer: (machines: string[]) => `${machines.join(', ')}`,
    },
    { def: 'updateType', title: 'Supported Update', sortable: false, columnFilter: true },
    {
      def: 'releaseDate',
      title: 'Upload Date',
      sortable: true,
      columnFilter: false,
      transformer: (date: string) => {
        const d = new Date(date);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      },
    },
  ];
  editingId: any;

  private refreshTablesListener: ((event: Event) => void) | null = null;

  constructor(
    protected router: Router,
    protected http: HttpClient,
    private messageService: MessageService,
    private softwareUpdateService: SoftwareUpdateService,
    private updateFileParserService: UpdateFileParserService,
    private confirmationService: ConfirmationService,
    private cognitoService: CognitoService
  ) {
    this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
    this.loadFamilyMenuItems();
    this.loadUserGroups();
  }

  ngOnInit(): void {
    this.setupRefreshListener();
  }

  ngOnDestroy(): void {
    this.removeRefreshListener();
  }

  private setupRefreshListener(): void {
    this.refreshTablesListener = () => {
      this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
    };
    window.addEventListener('refreshTable', this.refreshTablesListener);
  }

  private removeRefreshListener(): void {
    if (this.refreshTablesListener) {
      window.removeEventListener('refreshTable', this.refreshTablesListener);
    }
  }

  loadFamilyMenuItems() {
    this.loadingFamilyMenu = true;
    this.softwareUpdateService.getFamilies().subscribe({
      next: (data) => {
        this.loadingFamilyMenu = false;
        const filteredFamilies = filterMenderFamilies(data);
        this.familyMenuItems = filteredFamilies.map((family: string) => ({
          label: family,
          command: () => this.selectFamilyForUpload(family)
        }));
      },
      error: (error) => {
        this.loadingFamilyMenu = false;
        // Fallback: only traditional families
        this.familyMenuItems = [
          {
            label: 'IOT base module',
            command: () => this.selectFamilyForUpload('IOT base module')
          },
          {
            label: 'iDEA',
            command: () => this.selectFamilyForUpload('iDEA')
          },
        ];
      }
    });
  }

  globalFilterFields = this.tableDefinition.map((row) => row.def);

  actionDefinition = [
    {
      label: 'Delete',
      icon: 'pi pi-times',
      styleClass: 'b',
      callback: (row: any) => this.confirmDelete(row),
      active: () => this.canManageSoftware,
    },
  ];

  private async loadUserGroups(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      if (idToken) {
        const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
        const groups: string[] = payload['cognito:groups'] || [];
        const isReadOnly = groups.some(
          (group) => group.includes('technician') || group.includes('technician-admin') || group.includes('global-admin')
        );
        this.canManageSoftware = !isReadOnly;
      }
    } catch (error) {
      console.error('Error loading user groups:', error);
      this.canManageSoftware = false;
    }
  }

  onColumnFilter(event: any) {
    this.filters = event.filters || {};
    if (this.filters['supportedMachines']) {
      this.filters['model'] = this.filters['supportedMachines'];
    }
    this.listSoftwares(this.pageRows, 0, this.orderColumn, this.orderDirection, this.filters);
  }

  onGlobalSearch(searchValue: string) {
    this.filters = { ...this.filters, search: searchValue || null };
    this.listSoftwares(this.pageRows, 0, this.orderColumn, this.orderDirection, this.filters);
  }

  listSoftwares(
    limit?: number, 
    offset?: number, 
    orderColumn?: string, 
    orderDirection?: 'asc' | 'desc',
    filters?: { [key: string]: any }
  ): void {
    this.datasource = [];
    this.loading = true;
    this.softwareUpdateService.getSoftwares(limit, offset, orderColumn, orderDirection, filters).subscribe({
      next: (data) => {
        this.datasource = data.data.map((item: any) => ({
          ...item,
          supportedMachines: item.model ?? item.supportedMachines,
        }));
        this.dataSourceLength = data.total;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load softwares: ${error.message}`
        });
      },
    });
  }

  refreshSoftwareList(): void {
    this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      const fileName = file.name.toLowerCase();

      const expectedExtension = this.selectedFamilyForUpload === 'IOT base module'|| this.selectedFamilyForUpload === 'iDEA' ? '.sp' : '.mender';
      if (!fileName.endsWith(expectedExtension)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid file',
          detail: `Only ${expectedExtension} files are accepted for ${this.selectedFamilyForUpload}.`,
        });
        return;
      }
      
      this.uploadedFile = file;
      
      if (fileName.endsWith('.sp')) {
        this.analyzeSpFile(file);
      } else if (fileName.endsWith('.mender')) {
        this.analyzeMenderFile(file);
      }
      const reader = new FileReader();
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (arrayBuffer) {
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          this.sha256 = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileName = file.name.toLowerCase();

      const expectedExtension = this.selectedFamilyForUpload === 'IOT base module' || this.selectedFamilyForUpload === 'iDEA' ? '.sp' : '.mender';
      if (!fileName.endsWith(expectedExtension)) {
        this.messageService.add({
          severity: 'error', 
          summary: 'Invalid file', 
          detail: `Only ${expectedExtension} files are accepted for ${this.selectedFamilyForUpload}.`
        });
        input.value = '';
        return;
      }
      
      this.uploadedFile = file;
      
      if (fileName.endsWith('.sp')) {
        this.analyzeSpFile(file);
      }
      
      this.softwareUpdateService.getFamilies(fileName.endsWith('.mender') ? 'mender' : 'traditional').subscribe({
        next: (data) => {
          this.families = data;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to load families: ${error.message}`
          });
        },
      });
      const reader = new FileReader();
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (arrayBuffer) {
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          this.sha256 = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

  async analyzeSpFile(file: File): Promise<void> {
    try {
      this.calculatedSha256 = await this.calculateFileHash(file);
      this.sha256 = this.calculatedSha256;
      const spInfo = await this.updateFileParserService.parseSpFile(file);
      if (spInfo.isValidFormat) {
        this.parsedFileInfo = spInfo;
        this.form.versionNumber = spInfo.packageSwVersion;
        this.form.description = spInfo.packageSwInfo;
        this.form.model = spInfo.machineModels.join(', ');
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'SP parse error',
        detail: error instanceof Error ? error.message : String(error),
      });
    }
  }

  async analyzeMenderFile(file: File): Promise<void> {
    try {
      const menderInfo = await this.updateFileParserService.parseMenderFile(file);
      if (menderInfo.isValidFormat) {
        this.parsedFileInfo = menderInfo;
        this.form.versionNumber = menderInfo.packageSwVersion;
        this.form.description = menderInfo.packageSwInfo;  
        //For mender, remove the first element which represents the family type, used for validation purposes
        const machineModelsForForm = [...menderInfo.machineModels];
        if (machineModelsForForm.length > 0) {
          machineModelsForForm.shift();
        }
        this.form.model = machineModelsForForm.join(', ');
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Mender parse error',
        detail: error instanceof Error ? error.message : String(error),
      });
    }
  }

  get uploadedFileName(): string {
    return this.uploadedFile ? this.uploadedFile.name : '';
  }

  onAssignedPageChange(event: any) {
    this.pageRows = event.rows;
    this.offset = event.first;
    this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }

  assignedSortChangedEvent(event: any) {
    this.orderColumn = this.mapFieldToBackend(event.field);
    this.orderDirection = event.order === 1 ? 'asc' : 'desc';
    this.offset = 0;
    this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }

  clearAssignedFilters() {
		this.orderColumn = 'release_date';
		this.orderDirection = 'desc';
    this.filters = {};
    this.offset = 0;
    this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
  }

  private mapFieldToBackend(field: string): string {
    const mapping: Record<string, string> = {
      id: 'id',
      versionNumber: 'version_number',
      supportedMachines: 'model',
      family: 'family',
      releaseDate: 'release_date',
      description: 'description',
      createdAt: 'created_at',
      updatedAt: 'updated_at',
      sha256Hash: 'sha256_hash',
    };
    return mapping[field];
  }

  onNext() {
    if (this.parsedFileInfo && this.selectedFamilyForUpload && this.uploadedFile) {
      const fileName = this.uploadedFile.name.toLowerCase();
      if (fileName.endsWith('.sp')) {
        if (!this.validateSha256()) {
          this.messageService.add({
            severity: 'error',
            summary: 'SHA256 Validation Failed',
            detail: this.sha256ValidationError || 'Please provide a valid SHA256 hash for the uploaded file.',
            life: 10000
          });
          return;
        }
        this.showFormStep = true;
        return;
      }
      if (fileName.endsWith('.mender')) {
        const validation = this.updateFileParserService.validateFileCompatibility(
          this.parsedFileInfo,
          this.selectedFamilyForUpload
        );
        if (!validation.isValid) {
          this.messageService.add({
            severity: 'error',
            summary: 'File Incompatible',
            detail: validation.errorMessage || 'The selected file is not compatible with the chosen family.',
            life: 10000
          });
          return;
        }
      }
    }
    this.showFormStep = true;
  }

  onBack() {
    this.uploadedFile = null;
    this.sha256 = '';
    this.parsedFileInfo = null;
    this.showFormStep = false;
    this.uploading = false;
    
    this.userProvidedSha256 = '';
    this.calculatedSha256 = '';
    this.sha256ValidationError = '';
    this.isValidatingSha256 = false;
    
    this.form = {
      family: this.selectedFamilyForUpload,
      model: '',
      versionNumber: '',
      description: '',
    };
  }

  isFormValid(): boolean {
    return (
      this.form.family.trim() !== '' &&
      this.form.model.trim() !== '' &&
      this.form.versionNumber.trim() !== ''
    );
  }

  onUpload() {
    this.uploading = true;
    if (this.uploadedFile && this.uploadedFile.size > 10 * 1024 * 1024) {
      this.messageService.add({
        severity: 'info',
        summary: 'Large File Upload',
        detail: 'This is a large file (>10MB). Please wait, this may take a while.',
        life: 8000
      });
    }
    
    this.softwareUpdateService
      .uploadSoftwareRequest(
        this.form.family,
        this.form.model,
        this.form.versionNumber,
        this.sha256,
        this.uploadedFile ? this.uploadedFile.size : undefined,
        environment.production ? 'prod' : 'dev',
        this.form.description,
        this.uploadedFile ? this.uploadedFile.name : ''
      )
      .subscribe({
        next: (result) => {
          if (result && result.signedLink && this.uploadedFile) {
            this.http
              .put(result.signedLink, this.uploadedFile, {
                headers: {
                  'Content-Type': 'application/octet-stream',
                },
              })
              .subscribe({
                next: () => {
                  this.uploading = false;
                  this.showUploadModal = false;
                  this.messageService.add({
                    severity: 'success',
                    summary: 'Upload Successful',
                    detail: 'Software uploaded successfully!',
                  });
                  this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
                },
                error: () => {
                  this.uploading = false;
                  this.showUploadModal = false;
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Upload Failed',
                    detail: 'File upload failed. Please try again.',
                  });
                },
              });
          } else {
            this.uploading = false;
          }
        },
        error: (err) => {
          this.uploading = false;
          this.showUploadModal = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Upload Failed',
            detail: err.error?.message || 'Upload request failed. Please try again.',
          });
        },
      });
  }

  editColumnClicked(event: any): void {
    this.editingId = event.rowData.id;
    this.editingValue = event.columnDefinition.def;
    this.editForm.editing = event.rowData[this.editingValue];
    this.showEditModal = true;
  }

  onEditFormSubmit() {
    this.editing = true;
    let payload: any = {};
    if (this.editingValue === 'supportedMachines') {
      payload = { model: this.editForm.editing };
    } else if (this.editingValue === 'description') {
      payload = { description: this.editForm.editing };
    }
    
    this.cognitoService.getUser().then((user) => {
      if (!user.id) {
        throw new Error('User ID not available');
      }
      const userId = user.id;
      this.softwareUpdateService.editSoftwareInfo(this.editingId, userId, payload).subscribe({
        next: (result) => {
          this.editing = false;
          this.showEditModal = false;
          this.editingId = '';
          this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
          this.messageService.add({
            severity: 'success',
            summary: 'Edit Successful',
            detail: 'Software information updated successfully!',
          });
        },
        error: (err) => {
          this.editing = false;
          this.showEditModal = false;
          this.editingId = '';
          console.error('Edit failed:', err);
        },
      });
    }).catch((error) => {
      this.editing = false;
      this.showEditModal = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Authentication Error',
        detail: 'Failed to get user information. Please try again.',
      });
      console.error('Failed to get current user:', error);
    });
  }

  confirmDelete(row: any) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this software version?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteSoftware(row.id);
      }
    });
  }

  deleteSoftware(id: string) {
    this.softwareUpdateService.deleteSoftware(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Delete Successful',
          detail: 'Software version deleted successfully!',
        });
        this.listSoftwares(this.pageRows, this.offset, this.orderColumn, this.orderDirection, this.filters);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Delete Failed',
          detail: err.error?.message || 'Failed to delete software version. Please try again.',
        });
      },
    });
  }

  selectFamilyForUpload(family: string) {
    this.selectedFamilyForUpload = family;
    this.form.family = family;
    this.showUploadModal = true;
  }

  get allowedFileTypes(): string {
    return this.selectedFamilyForUpload === 'IOT base module' ||  this.selectedFamilyForUpload === 'iDEA' ? '.sp' : '.mender';
  }

  get fileTypeMessage(): string {
    return this.selectedFamilyForUpload === 'IOT base module' ||  this.selectedFamilyForUpload === 'iDEA'
      ? 'The only accepted file format is .sp'
      : 'The only accepted file format is .mender';
  }

  get isNextButtonEnabled(): boolean {
    if (!this.uploadedFile || !this.parsedFileInfo) {
      return false;
    }
    
    if (this.isSpFile()) {
      return this.validateSha256();
    }
    
    return true;
  }

  private validateSha256(): boolean {
    if (!this.isSpFile() || !this.userProvidedSha256.trim() || !this.calculatedSha256) {
      return false;
    }
    
    const userSha256 = this.userProvidedSha256.trim().toLowerCase();
    const calculatedSha256 = this.calculatedSha256.toLowerCase();
    
    if (userSha256 === calculatedSha256) {
      this.sha256ValidationError = '';
      return true;
    } else {
      this.sha256ValidationError = 'SHA256 mismatch! The provided SHA256 does not match the calculated hash of the uploaded file.';
      return false;
    }
  }

  isSpFile(): boolean {
    return this.selectedFamilyForUpload === 'IOT base module' || this.selectedFamilyForUpload === 'iDEA';
  }

  onSha256Input(): void {
    if (this.isSpFile() && this.userProvidedSha256.trim() && this.calculatedSha256) {
      this.validateSha256();
    }
  }

  private async calculateFileHash(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('Error calculating file hash:', error);
      return '';
    }
  }
}
